import { getCurrentScope, onScopeDispose, ref } from 'vue'

const DEFAULT_POLLING_INTERVAL_SECONDS = 5

export function useDashboardPolling() {
  const isRefreshing = ref(false)
  const isPollingEnabled = ref(false)
  const pollingIntervalSeconds = ref(DEFAULT_POLLING_INTERVAL_SECONDS)

  let refreshHandler: (() => void | Promise<void>) | null = null
  let pollingTimer: ReturnType<typeof setInterval> | null = null

  function registerRefresh(onRefresh: () => void | Promise<void>) {
    refreshHandler = onRefresh
  }

  async function runRefresh() {
    if (!refreshHandler || isRefreshing.value) return

    isRefreshing.value = true
    try {
      await refreshHandler()
    } finally {
      isRefreshing.value = false
    }
  }

  function stopPolling() {
    if (pollingTimer !== null) {
      clearInterval(pollingTimer)
      pollingTimer = null
    }
  }

  function startPolling() {
    stopPolling()
    if (!isPollingEnabled.value) return

    pollingTimer = setInterval(() => {
      void runRefresh().catch(() => undefined)
    }, pollingIntervalSeconds.value * 1000)
  }

  function setPollingEnabled(enabled: boolean) {
    isPollingEnabled.value = enabled

    if (enabled) {
      startPolling()
    } else {
      stopPolling()
    }
  }

  function setPollingIntervalSeconds(seconds: number) {
    if (!Number.isFinite(seconds) || seconds <= 0) return

    pollingIntervalSeconds.value = seconds
    if (isPollingEnabled.value) startPolling()
  }

  if (getCurrentScope()) {
    onScopeDispose(stopPolling)
  }

  return {
    isRefreshing,
    isPollingEnabled,
    pollingIntervalSeconds,
    registerRefresh,
    runRefresh,
    setPollingEnabled,
    setPollingIntervalSeconds,
    startPolling,
    stopPolling,
  }
}
